/**
 * Runtime icon registry.
 *
 * P3-021: All icons referenced by experience contracts resolve through this registry.
 * IconIds are typed in the contracts; the renderer imports the MUI icon lazily
 * or via a mapping to avoid ad-hoc icon imports in pages.
 */
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ImportIcon from '@mui/icons-material/FileUpload';
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import TuneIcon from '@mui/icons-material/Tune';

import type { IconId } from '@finance/contracts';
import { BrandDashboardIcon, BrandPortfolioIcon, BrandSettingsIcon } from './brand-icons';

export type IconComponent = ComponentType<SvgIconProps>;

const ICON_MAP: Record<string, IconComponent> = {
  dashboard: BrandDashboardIcon,
  accountBalance: AccountBalanceIcon,
  transactions: ReceiptLongIcon,
  investments: TrendingUpIcon,
  holdings: BrandPortfolioIcon,
  reports: AssessmentIcon,
  settings: BrandSettingsIcon,
  users: PeopleIcon,
  security: SecurityIcon,
  export: FileDownloadIcon,
  add: AddIcon,
  edit: EditIcon,
  delete: DeleteIcon,
  save: SaveIcon,
  cancel: CancelIcon,
  success: CheckCircleIcon,
  error: ErrorIcon,
  warning: WarningIcon,
  info: InfoIcon,
  arrowForward: ArrowForwardIcon,
  arrowBack: ArrowBackIcon,
  refresh: RefreshIcon,
  filter: FilterListIcon,
  more: MoreVertIcon,
  organization: BusinessIcon,
  category: CategoryIcon,
  batch: PlaylistAddIcon,
  reconciliation: CompareArrowsIcon,
  groups: GroupsIcon,
  lock: LockIcon,
  business: BusinessIcon,
  receipt: ReceiptIcon,
  import: ImportIcon,
  person: PersonIcon,
  key: KeyIcon,
  tune: TuneIcon,
};

export function getIcon(iconId: IconId | undefined): IconComponent | undefined {
  if (!iconId) return undefined;
  return ICON_MAP[iconId] ?? undefined;
}

export function resolveIcon(iconId: IconId | undefined): IconComponent {
  return getIcon(iconId) ?? InfoIcon;
}

export function registerIcon(iconId: IconId, component: IconComponent): void {
  ICON_MAP[iconId] = component;
}

export function isRegisteredIcon(iconId: string): iconId is IconId {
  return iconId in ICON_MAP;
}
